'use client'

import Cookie from 'js-cookie'
import Link from 'next/link'
import { useEffect, useState } from 'react'

import Container from '@/components/templates/container/container'
import { SiteLayout } from '@/components/templates/site-layout'

import { getCookies } from '@/lib/utils/get-cookies'

export interface LeadsData {
  gaClientId: string
  analyticsData: utmType
}

const HubungiKamiWa = () => {
  const [isSendWAFailed, setIsSendWAFailed] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)

  const createWALink = (phone: string, template: string, params: { [key: string]: string }): string => {
    let message = template
    for (const key of Object.keys(params)) {
      const pattern = new RegExp(`\\{${key}\\}`, 'g')
      message = message.replace(pattern, params[key])
    }

    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
  }

  const getMessageTemplate = (): string | null => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.has('message')) {
        return params.get('message')
      }

      return null
    }

    return null
  }

  const reportLeads = async (leads: LeadsData): Promise<{ ticketId: number; phoneNumber: string }> => {
    if (typeof window !== 'undefined') {
      const url = `${process.env.NEXT_PUBLIC_API_NETLIFY_URL || 'https://easybiz-id.netlify.app'}/.netlify/functions/report-leads`
      const res = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(leads)
      })
      if (!res.ok) {
        throw new Error(res.statusText)
      }

      return res.json()
    }
    throw new Error('Window is undefined')
  }

  const getGAClientID = (): string => {
    if (typeof window !== 'undefined') {
      return Cookie.get('_ga') ?? '-'
    }

    return '-'
  }

  const getAnalyticsData = (): utmType | null => {
    const cookieValue = getCookies('utmdt')
    if (cookieValue) {
      try {
        return JSON.parse(cookieValue)
      } catch (error) {
        console.error('Error parsing analytics data:', error)

        return null
      }
    }

    return null
  }

  useEffect(() => {
    let isExecuted = false

    const fetchData = async () => {
      if (isExecuted) return
      isExecuted = true

      const analyticsData = getAnalyticsData() ?? {
        utmSource: '-',
        utmMedium: '-',
        utmCampaign: '-',
        utmContent: '-',
        utmTerms: '-',
        source: '-',
        medium: '-'
      }

      try {
        const gaClientId = getGAClientID()
        const { ticketId, phoneNumber } = await reportLeads({
          gaClientId,
          analyticsData
        })

        const defaultTemplate =
          'Halo, saya ingin menghubungi Easybiz. Nomor tiket saya {ticket}, silahkan dicatat untuk sesi konsultasi'
        const template = getMessageTemplate() ?? defaultTemplate
        const link = createWALink(phoneNumber, template, { ticket: ticketId.toString() })

        setIsRedirecting(true)

        window.location.href = link
      } catch (err) {
        console.error('error:', err)
        setIsSendWAFailed(true)
      }
    }

    fetchData()
  }, [])

  return (
    <SiteLayout>
      <Container className="flex items-center justify-center h-[70vh] max-w-screen-sm">
        <section>
          <div className="text-center">
            <h1 className="sr-only">Hubungi Kami via Whatsapp - Easybiz</h1>
            {!isRedirecting && !isSendWAFailed && (
              <div className="space-y-2">
                <h2 className="text-xl font-semibold lg:text-2xl text-primary-blue">Mohon ditunggu...</h2>
                <p className="text-sm lg:text-base">
                  Anda sedang dihubungkan dengan salah seorang konsultan kami melalui Whatsapp
                </p>
              </div>
            )}
            {!isRedirecting && isSendWAFailed && (
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-red-600 lg:text-2xl">Gagal menghubungi konsultan</h2>
                <p className="text-sm lg:text-base">
                  Maaf telah terjadi kesalahan pada sistem kami, mohon refresh halaman, atau dapat kembali ke{' '}
                  <Link href="/" className="underline text-primary-blue">
                    beranda
                  </Link>
                </p>
              </div>
            )}
            {isRedirecting && (
              <div className="space-y-2">
                <h2 className="text-xl font-semibold lg:text-2xl text-primary-blue">Redirecting...</h2>
              </div>
            )}
          </div>
        </section>
      </Container>
    </SiteLayout>
  )
}

export default HubungiKamiWa
